import { useInfiniteQuery } from "@tanstack/react-query";
import { ArrowUpRight, Play, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import type { PlaygroundHistoryItem, PlaygroundHistoryScope } from "../../api/types";
import { useGatewayPermissions } from "../../auth/permissions";
import { DataState, formatMoney, formatTime, StatusBadge } from "../../components/ui";
import type { LightboxRequest } from "./PlaygroundLightbox";
import { historyMedia, parseGenerationStatus, playgroundRoutingLabel } from "./playgroundLogic";

// PlaygroundHistory lists playground requests with a mine/all scope and cursor pagination.
// 生成记录：支持“我的 / 全部”切换和游标分页加载。
export function PlaygroundHistory({ onPreview }: { onPreview: (request: LightboxRequest) => void }) {
  const [scope, setScope] = useState<PlaygroundHistoryScope>("mine");
  const sentinelRef = useRef<HTMLDivElement>(null);
  const history = useInfiniteQuery({
    queryKey: ["playground-history", scope],
    queryFn: ({ pageParam }) => api.playgroundHistory(scope, pageParam),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.data.next_cursor || undefined,
    retry: false,
  });
  const items = useMemo(() => {
    const seen = new Set<string>();
    return (history.data?.pages || []).flatMap((page) => page.data.items).filter((item) => !seen.has(item.request_id) && Boolean(seen.add(item.request_id)));
  }, [history.data]);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = history;

  // Auto-load the next page when the sentinel scrolls into view; the button remains as a keyboard fallback.
  // 滚动到底部自动加载下一页；“加载更多”按钮保留给键盘和不支持观察器的环境。
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    }, { rootMargin: "240px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return <section className="content-section playground-history" aria-labelledby="playground-history-title">
    <div className="section-heading">
      <div><h2 id="playground-history-title">生成记录</h2><p>所有记录都归属测试 Consumer，发起人来自登录账号。</p></div>
      <div className="segmented-control" role="group" aria-label="记录范围">
        {([["mine", "我的"], ["all", "全部"]] as const).map(([value, label]) => <button type="button" key={value} aria-pressed={scope === value} className={scope === value ? "active" : ""} onClick={() => setScope(value)}>{label}</button>)}
      </div>
    </div>
    <DataState loading={history.isLoading} error={history.isFetchNextPageError ? null : history.error} empty={!items.length} emptyTitle={scope === "mine" ? "你还没有发起过生成测试" : "还没有生成测试记录"} emptyDescription="在上方提交一次图片或视频生成后，这里会显示记录">
      <ul className="playground-history-grid">{items.map((item) => <HistoryCard key={item.request_id} item={item} onPreview={onPreview} />)}</ul>
      <div ref={sentinelRef} className="cursor-actions playground-history-more">
        <span>已加载 {items.length} 条</span>
        {history.isFetchNextPageError ? <span className="field-error-text" role="alert">加载失败：{history.error?.message}</span> : null}
        {hasNextPage ? <button type="button" className="secondary-button" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
          {isFetchingNextPage ? <><RefreshCw size={15} className="spin" />加载中</> : "加载更多"}
        </button> : <span className="muted">没有更多记录</span>}
      </div>
    </DataState>
  </section>;
}

function HistoryCard({ item, onPreview }: { item: PlaygroundHistoryItem; onPreview: (request: LightboxRequest) => void }) {
  const { can } = useGatewayPermissions();
  const media = historyMedia(item);
  const view = parseGenerationStatus(item.status, item.error_message);
  const cover = media[0];
  const modalityLabel = item.modality === "video" ? "视频" : "图片";
  const placeholder = !view.terminal ? "生成中" : view.succeeded ? "无可预览结果" : view.status === "uncertain" ? "结果不确定" : "生成失败";
  return <li className="playground-history-card">
    {cover
      ? <button type="button" className="playground-media-button playground-history-thumb" aria-label={`预览 ${item.requested_model} ${modalityLabel}结果`} onClick={() => onPreview({ items: media, startIndex: 0, title: `${item.requested_model} · ${formatTime(item.created_at)}` })}>
        {cover.type === "video" ? <><video src={cover.url} muted preload="metadata" aria-hidden="true" /><span className="playground-play"><Play size={18} /></span></> : <img src={cover.url} alt="" loading="lazy" />}
        {media.length > 1 ? <span className="playground-media-count">{media.length} 张</span> : null}
      </button>
      : <div className={`playground-history-thumb is-empty is-${view.tone}`}>{!view.terminal ? <RefreshCw size={16} className="spin" /> : null}{placeholder}</div>}
    <div className="playground-history-body">
      <div className="playground-history-meta"><StatusBadge value={view.status} /><span>{modalityLabel}</span>{item.mine ? <span className="playground-mine">我发起的</span> : null}</div>
      <strong>{item.requested_model}</strong>
      <p className="cell-note">{playgroundRoutingLabel(item.playground)}</p>
      <p className="cell-note">实际模型：{item.executed_model || "尚未执行"} · 渠道：{item.executed_channel || "尚未选定"}</p>
      {item.prompt ? <p className="playground-history-prompt" title={item.prompt}>{item.prompt}</p> : null}
      {view.status === "failed" && item.error_message ? <p className="cell-note error-note">{item.error_message}</p> : null}
      {view.status === "uncertain" ? <p className="cell-note error-note">{view.message}</p> : null}
      <p className="playground-history-foot"><span>{item.initiator || "未知发起人"}</span><span>{formatTime(item.created_at)}</span><span>{formatMoney(item.total_cost, item.currency)}</span></p>
      {can("requests.read") ? <Link className="row-link" to={`/manage/requests/${encodeURIComponent(item.request_id)}`} aria-label={`查看请求 ${item.request_id} 的详情`}>请求详情 <ArrowUpRight size={14} /></Link> : null}
    </div>
  </li>;
}
